import { useCallback, useEffect, useRef, useState } from 'react';
import { Base64 } from 'js-base64';
import { normalizeLegacyQuillHtml } from 'utils/canmail/normalizeLegacyQuillHtml';
import type { EditorContent, EditorType } from '../types';

/**
 * Owns the email body across both editors.
 *
 * ## Why this hook exists
 *
 * This is the seam for the **HTML editor** swap.
 *
 * The editor-agnostic surface (`mode`, `bodyHtml`, `setBody`, `getFinalHtml`, `isEmpty`,
 * `reset`) is what everything *outside* the editor components uses: send, schedule, test
 * send and template save all go through `getFinalHtml()` and never read a per-editor
 * field. `EditorPane` is the deliberate exception — it renders both editors, so it binds
 * to `setTextContent` / `setDropHtml` directly.
 *
 * **To swap the non-Drop editor:** change the component `EditorPane` renders for the
 * non-Drop slot, and point `textContent` / `setTextContent` / `loadTextContent` at
 * whatever it needs. Nothing outside this hook and that one component should have to
 * change. (`setBody` exists for exactly that wiring — it is the editor-agnostic setter
 * the editor's `onChange` can bind to.)
 *
 * Anything named `drop*` is Unlayer-specific and only exists because `DropEditor` needs
 * an imperative handle, a design to load, and a remount key. When the Drop editor is
 * eventually replaced too, that is the only group that has to change.
 */
export interface UseEmailBodyResult {
  // --- Editor-agnostic surface (prefer these) --------------------------------
  /** Which editor is active. */
  mode: EditorType;
  /** Rendered HTML of the active editor. This is what gets emailed. */
  bodyHtml: string;
  /** Replace the active editor's content. */
  setBody: (html: string) => void;
  /**
   * Freshest `{ html, designJson }` for the active editor.
   *
   * The Drop editor's state is only refreshed when Unlayer fires `design:updated`, so
   * every path that sends or persists the body must call this first rather than
   * reading `bodyHtml` directly.
   */
  getFinalHtml: () => Promise<EditorContent>;
  /** True when the body has no visible content (no text, and no image/table/hr). */
  isEmpty: (html?: string) => boolean;
  /** Clear all body content in both editors. */
  reset: () => void;

  // --- Mode -------------------------------------------------------------------
  isDropEditor: boolean;
  setIsDropEditor: (value: boolean) => void;
  /** False when the viewport is too narrow for Unlayer. */
  canUseDropEditor: boolean;
  /**
   * A Drop design is loaded but cannot be edited at this width. Callers must block
   * saving/sending — otherwise the empty text editor overwrites the real design.
   */
  hasUneditableDropContent: boolean;

  // --- Per-editor content (needed by the modals and API payloads) -------------
  textContent: string;
  setTextContent: (html: string) => void;
  dropHtml: string;
  setDropHtml: (html: string) => void;
  dropDesignJson: string;
  setDropDesignJson: (json: string) => void;

  // --- Unlayer-specific plumbing ----------------------------------------------
  dropDesignToLoad: string;
  dropEditorKey: number;
  editorRef: React.MutableRefObject<any>;
  /**
   * Switch to the Drop editor and load a base64 Unlayer design.
   *
   * `remount` forces the editor to re-mount, which is how a *replacement* design gets
   * picked up reliably. `clearText` wipes the text editor — correct when loading a
   * whole different email, but **not** when merely loading a template, since that
   * would silently discard the user's other-editor work.
   */
  loadDropDesign: (
    designJson: string,
    options?: { renderedHtml?: string; remount?: boolean; clearText?: boolean }
  ) => void;
  /**
   * Switch to the text editor with the given HTML.
   * `clearDrop` has the same caveat as `clearText` above.
   */
  loadTextContent: (html: string, options?: { clearDrop?: boolean }) => void;
  /** Insert a survey button. Returns false when the editor isn't ready. */
  insertSurveyButton: (label: string, link: string) => boolean;
}

/**
 * Tags that make a body meaningful even with no text, so an image-only (or table-only)
 * email is correctly treated as non-empty. `[\s/>]` matches the self-closing (`<hr/>`)
 * and attribute (`<img …>`) forms as well as `<table>`.
 */
const MEANINGFUL_EMPTY_TAGS = /<(img|table|hr|video|iframe)[\s/>]/i;

/**
 * True when an HTML body has no visible content.
 *
 * A string-equality list against a fixed set of "empty" markers does not survive an
 * editor swap — each editor stamps its own empty body (ReactQuill: `<p><br></p>`;
 * TinyMCE: `<p>&nbsp;</p>` or `<p><br data-mce-bogus="1"></p>`). Missing one silently
 * lets a blank email pass the "content required" check and get sent. So strip tags and
 * entities and ask whether any text is actually left — which is editor-agnostic and also
 * correct for legacy content already in the database.
 */
function isHtmlEmpty(html?: string): boolean {
  if (!html) return true;
  if (MEANINGFUL_EMPTY_TAGS.test(html)) return false;
  const text = html
    .replace(/<[^>]*>/g, '') // strip tags
    .replace(/&nbsp;|&#160;|&#xa0;/gi, ' ') // nbsp entity + numeric variants
    // A literal non-breaking space (U+00A0) is matched by \s and collapsed below.
    .replace(/\s+/g, ' ')
    .trim();
  return text.length === 0;
}

/** How long to wait for Unlayer's export callback before giving up. */
const EXPORT_TIMEOUT_MS = 5000;

export function useEmailBody(canUseDropEditor: boolean): UseEmailBodyResult {
  const [isDropEditor, setIsDropEditor] = useState(true);
  const [textContent, setTextContent] = useState('');
  const [dropHtml, setDropHtml] = useState('');
  const [dropDesignJson, setDropDesignJson] = useState('');
  const [dropDesignToLoad, setDropDesignToLoad] = useState('');
  const [dropEditorKey, setDropEditorKey] = useState(0);

  const editorRef = useRef<any>(null);

  // Unlayer has no mobile support, so below the threshold the composer is
  // single-editor-only. This also covers resizing mid-compose. Widening again re-shows
  // the toggle but deliberately does not auto-switch back, since the user may have
  // started typing in the other editor.
  useEffect(() => {
    if (!canUseDropEditor && isDropEditor) {
      setIsDropEditor(false);
    }
  }, [canUseDropEditor, isDropEditor]);

  const hasUneditableDropContent = !canUseDropEditor && Boolean(dropDesignJson);

  const isEmpty = useCallback(
    (html?: string) => {
      const value = html ?? (isDropEditor ? dropHtml : textContent);
      return isHtmlEmpty(value);
    },
    [isDropEditor, dropHtml, textContent]
  );

  /**
   * Pull the current design out of Unlayer. Resolves to `null` when the editor is not
   * mounted/ready so callers can fall back to last-known state rather than sending
   * nothing.
   */
  const exportDropDesign = useCallback((): Promise<EditorContent | null> => {
    const editor = editorRef.current?.editor;
    if (!editor) return Promise.resolve(null);

    return new Promise((resolve) => {
      // Unlayer's callback is not guaranteed to fire; never hang the send button on it.
      const timeoutId = setTimeout(() => {
        console.warn('[useEmailBody] Drop editor export timed out; using last known content.');
        resolve(null);
      }, EXPORT_TIMEOUT_MS);

      try {
        editor.exportHtml((data: any) => {
          clearTimeout(timeoutId);
          try {
            resolve({
              html: data?.html || '',
              designJson: Base64.encode(JSON.stringify(data?.design ?? {})),
            });
          } catch (error) {
            console.error('[useEmailBody] Failed to encode exported design:', error);
            resolve(null);
          }
        });
      } catch (error) {
        clearTimeout(timeoutId);
        console.error('[useEmailBody] Drop editor export failed:', error);
        resolve(null);
      }
    });
  }, []);

  const getFinalHtml = useCallback(async (): Promise<EditorContent> => {
    if (!isDropEditor) {
      return { html: textContent, designJson: '' };
    }

    const exported = await exportDropDesign();
    if (exported && exported.html) {
      setDropHtml(exported.html);
      setDropDesignJson(exported.designJson);
      return exported;
    }

    return { html: dropHtml, designJson: dropDesignJson };
  }, [isDropEditor, textContent, dropHtml, dropDesignJson, exportDropDesign]);

  const setBody = useCallback(
    (html: string) => {
      if (isDropEditor) {
        setDropHtml(html);
      } else {
        setTextContent(html);
      }
    },
    [isDropEditor]
  );

  const loadDropDesign = useCallback(
    (
      designJson: string,
      options?: { renderedHtml?: string; remount?: boolean; clearText?: boolean }
    ) => {
      const { renderedHtml, remount = false, clearText = false } = options ?? {};

      setIsDropEditor(true);
      setDropDesignToLoad(designJson);
      setDropDesignJson(designJson);
      if (renderedHtml !== undefined) setDropHtml(renderedHtml);
      if (clearText) setTextContent('');
      // Unlayer only reliably adopts a replacement design on mount.
      if (remount) setDropEditorKey((prev) => prev + 1);
    },
    []
  );

  const loadTextContent = useCallback((html: string, options?: { clearDrop?: boolean }) => {
    const { clearDrop = false } = options ?? {};

    setIsDropEditor(false);
    // The one place existing content enters the text editor (templates, drafts, scheduled
    // emails), so it is where legacy ReactQuill formatting gets rescued into inline styles.
    setTextContent(normalizeLegacyQuillHtml(html));
    if (clearDrop) {
      setDropHtml('');
      setDropDesignJson('');
      setDropDesignToLoad('');
    }
  }, []);

  const insertSurveyButton = useCallback((label: string, link: string): boolean => {
    if (!editorRef.current?.insertButton) {
      console.error('[useEmailBody] Drop editor not ready; cannot insert survey button.');
      return false;
    }
    editorRef.current.insertButton(label, link);
    return true;
  }, []);

  const reset = useCallback(() => {
    setTextContent('');
    setDropHtml('');
    setDropDesignJson('');
    setDropDesignToLoad('');
    setDropEditorKey((prev) => prev + 1);
  }, []);

  return {
    mode: isDropEditor ? 'drop' : 'text',
    bodyHtml: isDropEditor ? dropHtml : textContent,
    setBody,
    getFinalHtml,
    isEmpty,
    reset,

    isDropEditor,
    setIsDropEditor,
    canUseDropEditor,
    hasUneditableDropContent,

    textContent,
    setTextContent,
    dropHtml,
    setDropHtml,
    dropDesignJson,
    setDropDesignJson,

    dropDesignToLoad,
    dropEditorKey,
    editorRef,
    loadDropDesign,
    loadTextContent,
    insertSurveyButton,
  };
}

export default useEmailBody;
